import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWalletActivity } from "@/lib/api";
import type { WalletActivity } from "@/types";

/**
 * ALL-5458: incoming funds for one wallet, kept fresh while the page is open.
 *
 * The founder's requirement is that a friend sees a test transfer "immediately", so the
 * feed polls on a short interval instead of waiting for a manual Refresh. It pauses while
 * the tab is hidden (no requests for a page nobody is looking at) and refetches the moment
 * the tab becomes visible again. `onChange` fires when an entry appears or its status
 * changes after the first load, so the caller can refresh the balances that transfer moved.
 */
export const WALLET_ACTIVITY_POLL_MS = 8_000;

export function activitySignature(activity: WalletActivity | null): string {
  if (!activity) return "";
  return activity.activity.map((e) => `${e.id}:${e.status}`).join("|");
}

interface UseWalletActivityResult {
  activity: WalletActivity | null;
  loading: boolean;
  refresh: () => void;
}

export function useWalletActivity(
  walletId: string,
  onChange?: () => void,
  pollMs: number = WALLET_ACTIVITY_POLL_MS,
): UseWalletActivityResult {
  const [activity, setActivity] = useState<WalletActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const signature = useRef<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const load = useCallback(async (): Promise<void> => {
    const next = await fetchWalletActivity(walletId);
    const nextSignature = activitySignature(next);
    // A failed poll must not wipe entries the customer is already looking at.
    setActivity((previous) =>
      next.monitoring === "UNAVAILABLE" && previous && previous.activity.length > 0
        ? { ...previous, monitoring: "UNAVAILABLE" }
        : next,
    );
    setLoading(false);
    if (next.monitoring === "UNAVAILABLE") return;
    if (signature.current !== null && signature.current !== nextSignature) {
      onChangeRef.current?.();
    }
    signature.current = nextSignature;
  }, [walletId]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const schedule = () => {
      if (cancelled) return;
      timer = setTimeout(async () => {
        if (typeof document === "undefined" || document.visibilityState !== "hidden") {
          await load();
        }
        schedule();
      }, pollMs);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };

    signature.current = null;
    setLoading(true);
    void load().then(schedule);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, pollMs]);

  return { activity, loading, refresh: () => void load() };
}
